import {
  type FeatureFlag,
  getFeatureFlagsOptions,
  getFeatureFlagsQueryKey,
} from "@sunner-cloud/api-client";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { type FlagName } from "../_context/FeatureFlagsContext";
import { useAuth } from "../auth/useAuth";

/** @internal */
export const FEATURE_FLAGS_QUERY_KEY = getFeatureFlagsQueryKey();

const STALE_TIME_MS = 0;
const GC_TIME_MS = 48 * 60 * 60 * 1000;
const REFETCH_INTERVAL_MS = 5 * 60 * 1000;

interface UseFeatureFlagsResult {
  flags: FeatureFlag[];
  flagsMap: Map<string, boolean>;
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
  error: Error | null;
  isEnabled: (flagName: FlagName) => boolean;
}

export const useFeatureFlags = (): UseFeatureFlagsResult => {
  const { isAuthenticated } = useAuth();

  const { data, isLoading, isSuccess, isError, error } = useQuery({
    ...getFeatureFlagsOptions(),
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    retry: 3,
    enabled: isAuthenticated,
  });

  const flags = useMemo(() => data ?? [], [data]);

  const flagsMap = useMemo(
    () => new Map(flags.map((flag) => [flag.name, flag.enabled])),
    [flags],
  );

  const isEnabled = useCallback(
    (flagName: FlagName): boolean => flagsMap.get(flagName) === true,
    [flagsMap],
  );

  return {
    flags,
    flagsMap,
    isLoading,
    isReady: isSuccess,
    isError,
    error,
    isEnabled,
  };
};
